import {
  DailyReportMostSailedParams,
  DailyReportParams,
  HourlyReportMostSailedParams,
  HourlyReportParams,
  MenuItemReportParams,
} from '@models/payload/report';
import { CancelOrderReportQueryResponseResponse } from '@models/response/order';
import {
  DownloadReportExcel,
  ReportInfoResponse,
  ReportMostSailedResponse,
  SnappFoodOrderReport,
  SnappFoodOrderReportQueryResponseResponse,
} from '@models/response/report';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import {
  cancelReportDailyService,
  cancelReportHourService,
  downcellExcelReports,
  downloadCancelHourlyExcelReports,
  downloadDailyExcelReports,
  downloadDailyOrdersExcelReports,
  downloadHourlyExcelReports,
  getDailyInfoReports,
  getDailyMostSailedReports,
  getDailyMostToppingSailedReports,
  getDailySnappFoodHourlyReports,
  getDailySnappFoodReports,
  getHourlyInfoReportsService,
  getHourlyMostSailedReports,
  getHourlyMostSailedToppingReports,
} from '@services/report';
import { createAppAsyncThunk } from '../utils/reduxUtil';

interface VendorReportsStateInterface {
  loading: boolean;
  foodLoading: boolean;
  reportInfo: ReportInfoResponse | undefined;
  snappFoodReport: SnappFoodOrderReport | undefined;
  cancelReportInfo: CancelOrderReportQueryResponseResponse | undefined;
  reportMostSailed: ReportMostSailedResponse | undefined;
  sailedLoading: boolean;
  excelDownload: DownloadReportExcel | undefined;
}

const initialState: VendorReportsStateInterface = {
  loading: false,
  foodLoading: false,
  reportInfo: undefined,
  cancelReportInfo: undefined,
  reportMostSailed: undefined,
  sailedLoading: false,
  excelDownload: undefined,
  snappFoodReport: undefined,
};

const getDailyInfoReport = createAppAsyncThunk<ReportInfoResponse, DailyReportParams>(
  'reports/getDailyInfoReport',
  async (input) => {
    const response = await getDailyInfoReports(input);
    return response;
  }
);

const getDailySailedReport = createAppAsyncThunk<ReportMostSailedResponse, DailyReportMostSailedParams>(
  'reports/getDailySailedReport',
  async (input) => {
    const response = await getDailyMostSailedReports(input);
    return response;
  }
);

const getDailyMostToppingReport = createAppAsyncThunk<ReportMostSailedResponse, DailyReportMostSailedParams>(
  'reports/getDailyToppingSailedReport',
  async (input) => {
    const response = await getDailyMostToppingSailedReports(input);
    return response;
  }
);

const downloadDailyExcel = createAppAsyncThunk<DownloadReportExcel, DailyReportMostSailedParams>(
  'reports/downloadDailyExcel',
  async (input) => {
    const response = await downloadDailyExcelReports(input);
    return response;
  }
);

const getHourlyInfoReport = createAppAsyncThunk<ReportInfoResponse, HourlyReportParams>(
  'reports/getHourlyInfoReport',
  async (input) => {
    const response = await getHourlyInfoReportsService(input);
    return response;
  }
);

const getHourlySailedReport = createAppAsyncThunk<ReportMostSailedResponse, HourlyReportMostSailedParams>(
  'reports/getHourlySailedReport',
  async (input) => {
    const response = await getHourlyMostSailedReports(input);
    return response;
  }
);

const getHourlySailedToppingReport = createAppAsyncThunk<ReportMostSailedResponse, HourlyReportMostSailedParams>(
  'reports/getHourlySailedToppingReport',
  async (input) => {
    const response = await getHourlyMostSailedToppingReports(input);
    return response;
  }
);

const downloadHourlyExcel = createAppAsyncThunk<DownloadReportExcel, HourlyReportMostSailedParams>(
  'reports/downloadHourlyExcel',
  async (input) => {
    const response = await downloadHourlyExcelReports(input);
    return response;
  }
);

const downloadOrderDailyExcelReport = createAppAsyncThunk<DownloadReportExcel, DailyReportParams>(
  'reports/downloadOrderDailyExcelReport',
  async (input) => {
    const response = await downloadDailyOrdersExcelReports(input);
    return response;
  }
);

const cancelHourlyReport = createAppAsyncThunk<CancelOrderReportQueryResponseResponse, HourlyReportParams>(
  'reports/cancelReport',
  async (input) => {
    const response = await cancelReportHourService(input);
    return response;
  }
);
const cancelDailyReport = createAppAsyncThunk<CancelOrderReportQueryResponseResponse, DailyReportParams>(
  'reports/cancelDailyReport',
  async (input) => {
    const response = await cancelReportDailyService(input);
    return response;
  }
);

const downloadCancelDailyExcel = createAppAsyncThunk<DownloadReportExcel, DailyReportParams>(
  'reports/downloadCancelDailyExcel',
  async (input) => {
    const response = await downloadCancelHourlyExcelReports(input);
    return response;
  }
);

const getSnappFoodDailyReport = createAppAsyncThunk<SnappFoodOrderReportQueryResponseResponse, DailyReportParams>(
  'reports/snappFoodDailyReport',
  async (input) => {
    const response = await getDailySnappFoodReports(input);
    return response;
  }
);

const getSnappFoodHourlyReport = createAppAsyncThunk<SnappFoodOrderReportQueryResponseResponse, HourlyReportParams>(
  'reports/snappFoodHourlyReport',
  async (input) => {
    const response = await getDailySnappFoodHourlyReports(input);
    return response;
  }
);

const downloadProductsExcelReports = createAppAsyncThunk<DownloadReportExcel, MenuItemReportParams>(
  'reports/downloadProductsExcelReports',
  async (input) => {
    const response = await downcellExcelReports(input);
    return response;
  }
);

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    emptyReport: (state) => ({
      ...state,
      reportInfo: undefined,
      reportMostSailed: undefined,
      excelDownload: undefined,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<VendorReportsStateInterface>) => {
    builder.addCase(getDailyInfoReport.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getDailyInfoReport.fulfilled, (state, action) => ({
      ...state,
      reportInfo: action.payload,
      loading: false,
    }));
    builder.addCase(getDailyInfoReport.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(getDailySailedReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));
    builder.addCase(getDailySailedReport.fulfilled, (state, action) => ({
      ...state,
      reportMostSailed: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(getDailySailedReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(getDailyMostToppingReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));
    builder.addCase(getDailyMostToppingReport.fulfilled, (state, action) => ({
      ...state,
      reportMostSailed: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(getDailyMostToppingReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(getHourlyInfoReport.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getHourlyInfoReport.fulfilled, (state, action) => ({
      ...state,
      reportInfo: action.payload,
      loading: false,
    }));
    builder.addCase(getHourlyInfoReport.rejected, (state) => ({
      ...state,
      loading: false,
    }));

    builder.addCase(getHourlySailedReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));

    builder.addCase(getHourlySailedReport.fulfilled, (state, action) => ({
      ...state,
      reportMostSailed: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(getHourlySailedReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(downloadDailyExcel.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));

    builder.addCase(downloadDailyExcel.fulfilled, (state, action) => ({
      ...state,
      excelDownload: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(downloadDailyExcel.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(downloadHourlyExcel.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));

    builder.addCase(downloadHourlyExcel.fulfilled, (state, action) => ({
      ...state,
      excelDownload: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(downloadHourlyExcel.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(getHourlySailedToppingReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));
    builder.addCase(getHourlySailedToppingReport.fulfilled, (state, action) => ({
      ...state,
      reportMostSailed: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(getHourlySailedToppingReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(downloadOrderDailyExcelReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));

    builder.addCase(downloadOrderDailyExcelReport.fulfilled, (state, action) => ({
      ...state,
      excelDownload: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(downloadOrderDailyExcelReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(cancelHourlyReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));

    builder.addCase(cancelHourlyReport.fulfilled, (state, action) => ({
      ...state,
      cancelReportInfo: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(cancelHourlyReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(cancelDailyReport.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));

    builder.addCase(cancelDailyReport.fulfilled, (state, action) => ({
      ...state,
      cancelReportInfo: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(cancelDailyReport.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(downloadCancelDailyExcel.pending, (state) => ({
      ...state,
      sailedLoading: true,
    }));
    builder.addCase(downloadCancelDailyExcel.fulfilled, (state, action) => ({
      ...state,
      excelDownload: action.payload,
      sailedLoading: false,
    }));
    builder.addCase(downloadCancelDailyExcel.rejected, (state) => ({
      ...state,
      sailedLoading: false,
    }));

    builder.addCase(getSnappFoodDailyReport.pending, (state) => ({
      ...state,
      foodLoading: true,
    }));
    builder.addCase(getSnappFoodDailyReport.fulfilled, (state, action) => ({
      ...state,
      snappFoodReport: action.payload.data,
      foodLoading: false,
    }));
    builder.addCase(getSnappFoodDailyReport.rejected, (state) => ({
      ...state,
      foodLoading: false,
    }));

    builder.addCase(getSnappFoodHourlyReport.pending, (state) => ({
      ...state,
      foodLoading: true,
    }));
    builder.addCase(getSnappFoodHourlyReport.fulfilled, (state, action) => ({
      ...state,
      snappFoodReport: action.payload.data,
      foodLoading: false,
    }));
    builder.addCase(getSnappFoodHourlyReport.rejected, (state) => ({
      ...state,
      foodLoading: false,
    }));
  },
});

export default reportSlice;
export {
  downloadDailyExcel,
  downloadHourlyExcel,
  downloadOrderDailyExcelReport,
  getDailyInfoReport,
  getDailyMostToppingReport,
  getDailySailedReport,
  getHourlyInfoReport,
  getHourlySailedReport,
  getHourlySailedToppingReport,
  cancelHourlyReport,
  cancelDailyReport,
  downloadCancelDailyExcel,
  getSnappFoodDailyReport,
  getSnappFoodHourlyReport,
  downloadProductsExcelReports,
};
